/**
 * Die Kapitel des Handbuchs — einmal, für Leiste, Suche und Seitenwechsel.
 *
 * ## Warum nicht einfach im Markup
 *
 * Weil die Leiste links und die Überschriften auf den Seiten sonst zwei
 * Listen wären, die dasselbe behaupten. Die Titel stehen deshalb hier, die
 * Leiste entsteht daraus, und die Überschrift jedes Kapitels nimmt denselben
 * Eintrag. Was im Markup **ausgeschrieben** bleibt, ist allein der
 * `id`-Anker — der Grund dafür steht in `anker.ts`.
 *
 * ## Warum ein `zeile` und nicht nur ein Titel
 *
 * Ein Verzeichnis aus sieben Substantiven („Erste Reise", „Unterwegs",
 * „Mitlesen") ist eine Liste von Wörtern, keine Orientierung. Der Halbsatz
 * daneben sagt, was drinsteht — damit man den Abschnitt findet, den man
 * sucht, ohne alle sieben zu öffnen.
 *
 * ## Warum seit dem 15.9.2026 `gruppe` und `einleitung` dazugehören
 *
 * Die Reihenfolge dieser Liste ist seit dem Umbau zum Handbuch die Ordnung
 * einer **Leiste** und nicht mehr die eines Fließtexts: Achtzehn Zeilen
 * untereinander sind keine Gliederung, fünf Gruppen mit drei bis fünf Zeilen
 * sind eine. Die Gruppe steht am Kapitel und nicht in einer zweiten Liste
 * daneben — sonst gibt es eines Tages ein Kapitel, das in keiner Gruppe
 * steht, und es fehlt in der Leiste, ohne dass etwas rot wird.
 *
 * Die `einleitung` ist der eine Satz unter dem Kapiteltitel. Auf der alten
 * langen Seite war sie unnötig: Wer oben anfing, hatte den Zusammenhang schon
 * gelesen. Wer über Suche oder Sprungmarke mitten in ein Kapitel fällt, hat
 * ihn nicht — und ein Kapitel, das ohne Vorrede mit einer Unterüberschrift
 * beginnt, liest sich wie die Mitte eines Gesprächs.
 */

/** Die fünf Gruppen der Leiste, in ihrer Reihenfolge. */
export type Kapitelgruppe = 'anfangen' | 'planen' | 'unterwegs' | 'danach' | 'nachschlagen';

/**
 * Wie die Gruppen in der Leiste heißen.
 *
 * Verben und Zeitpunkte statt Sachgebiete: „Anfangen", „Planen",
 * „Unterwegs", „Danach" sind die vier Zustände, in denen man das Handbuch
 * aufschlägt — und „Nachschlagen" ist der fünfte, der zu keinem Zeitpunkt
 * gehört. Eine Ordnung nach Bauteilen („Karte", „Konto", „Export") wäre die
 * Ordnung des Programms und nicht die der Reise.
 */
export const GRUPPEN: readonly { id: Kapitelgruppe; titel: string }[] = [
  { id: 'anfangen', titel: 'Anfangen' },
  { id: 'planen', titel: 'Planen' },
  { id: 'unterwegs', titel: 'Unterwegs' },
  { id: 'danach', titel: 'Danach' },
  { id: 'nachschlagen', titel: 'Nachschlagen' }
];

export interface Hilfeabschnitt {
  /** Der Anker. Steht im Markup ausgeschrieben; `anker.ts` gleicht beides ab. */
  id: string;
  titel: string;
  /** Was in diesem Abschnitt steht, in einem Halbsatz. */
  zeile: string;
  /** In welcher Gruppe der Leiste das Kapitel steht. */
  gruppe: Kapitelgruppe;
  /** Der eine Satz unter dem Kapiteltitel — für den, der mitten hineinfällt. */
  einleitung: string;
}

export const GLIEDERUNG: readonly Hilfeabschnitt[] = [
  {
    id: 'was-das-ist',
    titel: 'Was Roadtrip ist',
    zeile: 'wofür das Werkzeug gebaut ist — und für wen nicht',
    gruppe: 'anfangen',
    einleitung:
      'Wofür dieses Werkzeug gebaut ist, für wen es gedacht ist — und was es ausdrücklich nicht sein will.'
  },
  {
    id: 'erste-reise',
    titel: 'Die erste Reise',
    zeile: 'anlegen, die zwei Fragen, Stationen auf die Karte setzen',
    gruppe: 'anfangen',
    einleitung:
      'Von der leeren Reiseliste bis zu den ersten Orten auf der Karte.'
  },
  // Der Einstieg stand bis zum 15.9.2026 als Kasten **über** dem
  // Inhaltsverzeichnis und ausdrücklich nicht darin — er war die Abkürzung an
  // den Abschnitten vorbei, und ein Verzeichniseintrag hätte ihn zu einem von
  // sechzehn gemacht. Im Handbuch ist jedes Kapitel eine Seite, also braucht
  // auch er eine: Ohne Eintrag stünde die Abkürzung auf keiner Leiste und
  // wäre nur noch über die Suche zu finden. Er steht dafür in der Gruppe
  // „Anfangen" ganz hinten — wer schon weiß, was er will, liest die zwei
  // Kapitel darüber, und wer nicht, klickt hier.
  {
    id: 'einstieg',
    titel: 'In zehn Minuten zur ersten Reise',
    zeile:
      'die Abkürzung an allen Kapiteln vorbei: Beispielreise ansehen, eigene Reise anlegen, Route rechnen',
    gruppe: 'anfangen',
    einleitung:
      'Die Abkürzung an allen Kapiteln vorbei: drei Griffe für die erste Viertelstunde.'
  },
  // „Packliste" steht seit dem 11.9.2026 in der Zeile: Der Abschnitt erklärt
  // jetzt die bearbeitbare Liste samt Standard am Konto — und gesucht wird das
  // unter diesem Wort, nicht unter „Reihenfolge". Die Zeile ist zugleich das
  // Suchfutter der Befehlssuche (siehe `befehle.ts`).
  //
  // „Zeitzone" und „Ortszeit" stehen seit dem 14.9.2026 dabei: Der Abschnitt
  // erklärt jetzt, in welcher Zeit die Uhrzeiten des Plans stehen — und wer
  // das sucht, tippt genau diese zwei Wörter und nicht „So plant es sich gut".
  //
  // „Länder unterwegs", „Trinkgeld" und „Notruf" kamen am selben Tag dazu.
  // Niemand sucht Trinkgeld unter einer Planungsreihenfolge — gesucht wird es
  // unter dem Wort, das die Frage stellt.
  // „Seitenleiste", „Phasen" und „Menü" stehen seit dem 14.9.2026 vorn in der
  // Zeile: Der Abschnitt fängt seither mit „Wie du dich zurechtfindest" an,
  // und wer die Navigation sucht, tippt genau diese Wörter — nicht „So plant
  // es sich gut". Die Zeile ist zugleich das Suchfutter der Befehlssuche.
  {
    id: 'so-plant-es-sich',
    titel: 'So plant es sich gut',
    zeile:
      'wie du dich zurechtfindest: Seitenleiste, die fünf Phasen, das Menü auf dem Telefon — und die Reihenfolge, in der die Reise entsteht: sieben Schritte, Vorbereitung, Packliste, Zeitzone und Ortszeit, Länder unterwegs mit Trinkgeld, Notruf, Do\'s und Don\'ts, Sprache, Feiertage und Zeitzone',
    gruppe: 'planen',
    einleitung:
      'Wie du dich zurechtfindest — und in welcher Reihenfolge eine Reise entsteht.'
  },
  // Direkt hinter der Planungsreihenfolge und **vor** dem Stationsblatt: Der
  // Bereich Grundlagen ist der zweite Einstieg neben „Meine Reisen", und was
  // dort steht, gilt für jede Reise — das gehört gelesen, bevor es um die
  // Feinheiten einer einzelnen geht. Die Zeile nennt die Wörter, unter denen
  // man den Abschnitt sucht („Kennzeichen", „ZTL", „Zuhause"); sie ist
  // zugleich das Suchfutter der Befehlssuche (siehe `befehle.ts`).
  //
  // „Ladekarten" und „Reiseprofile" stehen seit dem Abend des 12.9.2026 dabei
  // (Runde 2). Bis dahin fehlten sie mit Absicht — eine Zeile, die einen
  // Abschnitt verspricht, den es nicht gibt, schickt jemanden auf die Suche.
  // Jetzt gibt es beide, und sie sind die Wörter, unter denen man sie sucht:
  // Niemand tippt „Grundlagen", wenn er wissen will, was der Strom kostet.
  {
    id: 'grundlagen',
    titel: 'Grundlagen',
    zeile:
      'was für jede Reise gilt: Fahrzeug mit Kennzeichen, Maßen, Vignetten und ZTL, Ladekarten mit Preisen je Land, die Standard-Packliste, Reiseprofile, Zuhause, Vertrauenspersonen, Freigaben, Abmelden',
    gruppe: 'planen',
    einleitung:
      'Was du einmal einträgst und was danach für jede Reise gilt.'
  },
  // Direkt hinter der Planungsreihenfolge und vor allem, was mit Claude,
  // unterwegs oder nach außen passiert: Es ist die Fläche, an der man beim
  // Planen die meiste Zeit verbringt. Die Zeile nennt die Wörter, unter denen
  // man den Abschnitt sucht — sie ist zugleich das Suchfutter der
  // Befehlssuche (siehe `befehle.ts`).
  {
    id: 'stationsblatt',
    titel: 'Das Stationsblatt',
    zeile: 'die rechte Spalte einer Station: Kennkopf, Sprungzeile, Abschnitte, Chips — und die Befehlssuche mit Cmd-K',
    gruppe: 'planen',
    einleitung:
      'Die rechte Spalte einer Station: die Fläche, an der beim Planen die meiste Zeit vergeht.'
  },
  {
    id: 'mit-claude',
    titel: 'Mit Claude planen',
    zeile: 'die Reise im Gespräch bauen — wenn dein Konto den Zugang hat; Roadtrip in Claude Desktop einrichten: Connector, MCP, Adresse, verbinden',
    gruppe: 'planen',
    einleitung:
      'Die Reise im Gespräch bauen — wenn dein Konto den Zugang dafür hat.'
  },
  // Ein eigener Abschnitt und kein Absatz im Mitlesen: Die beiden sehen sich
  // ähnlich und sind das Gegenteil voneinander — der eine gibt einen Link
  // heraus, der andere einen Zugang. Wer „Mitplanen" sucht, sucht es unter
  // diesem Wort; ihn dafür einen Abschnitt öffnen zu lassen, dessen Zeile
  // „was er nicht zeigt" verspricht, wäre eine Fährte in die falsche
  // Richtung. Direkt hinter dem Mitlesen, damit der Vergleich im Auge bleibt.
  {
    id: 'mitplanen',
    titel: 'Zu zweit planen',
    zeile: 'wenn der andere mitarbeiten soll statt nur zuzusehen',
    gruppe: 'planen',
    einleitung:
      'Wenn der andere mitarbeiten soll und nicht nur zusehen.'
  },
  // Hinter dem Stationsblatt und vor allem, was mit Claude oder unterwegs
  // passiert: Die Briefings sind eine **Lesart des Plans** und keine eigene
  // Funktion — sie stehen im Plan-Reiter, sie lesen nur, und der Warum-Satz,
  // von dem sie leben, wird eine Fläche weiter oben geschrieben (im
  // Stationsblatt). Der Abschnitt gehört deshalb neben die Planung und nicht
  // zu Claude, obwohl der Vorschlagsknopf dort nachschlägt.
  {
    id: 'briefings',
    titel: 'Was dich erwartet',
    zeile:
      'die Reise vorab, der Abend davor, die Lage vor Ort — und warum dieser Ort im Plan steht',
    gruppe: 'unterwegs',
    einleitung:
      'Was die Reise vorab, der Abend davor und die Lage vor Ort dir erzählen.'
  },
  {
    id: 'unterwegs',
    titel: 'Unterwegs mit der App',
    // „Navi" und „Koordinaten" stehen seit dem 5.9.2026 dabei: Der Abschnitt
    // erklärt jetzt auch, welcher Knopf das Ziel in welches Navi bringt — und
    // gesucht wird das unter diesen Wörtern, nicht unter „App". Seit dem
    // 11.9.2026 kommt der handfreie Start dazu, und er steht aus demselben
    // Grund mit seinen eigenen Wörtern da: Gesucht wird er unter „Siri“ und
    // „Kurzbefehl“, nicht unter „Start“. „CarPlay“ steht seit dem 12.9.2026
    // daneben — wer Lotte auf dem Autodisplay sucht, tippt den Markennamen
    // ein und nicht „Auto“ oder „Display“. Die Zeile ist zugleich das
    // Suchfutter der Befehlssuche (siehe `befehle.ts`).
    zeile:
      'Karte zum Mitnehmen, das Ziel ins Navi (Koordinaten), Lotte handfrei starten (Siri, Kurzbefehl, CarPlay), Tagesplan abhaken, Tagebuch, Station auslassen (Abstecher gehen mit, Kapitelmarke gilt weiter)',
    gruppe: 'unterwegs',
    einleitung:
      'Was das Telefon während der Fahrt übernimmt — und was am Rechner bleibt.'
  },
  // Direkt hinter „Unterwegs": Es ist dasselbe Gerät, dieselbe Fahrt und
  // dieselbe Hand am Lenkrad. Vor dem Mitlesen, weil dort die Reise nach außen
  // geht — hier bleibt sie im Auto.
  // Der Titel bleibt beschreibend und nimmt den Namen nicht auf: „Lotte" sagt
  // niemandem etwas, der die App noch nie geöffnet hat, und das Verzeichnis
  // ist genau die Stelle, an der man noch nichts weiß. Der Name steht deshalb
  // in der Zeile darunter, wo er sich selbst erklärt.
  {
    id: 'erzaehlen-lassen',
    titel: 'Unterwegs erzählen lassen',
    // Seit dem 27.8.2026 schlägt Lotte auch etwas vor. Die Zeile nennt beides,
    // und die Reihenfolge ist die der Sache: Erzählen ist, wofür man sie
    // startet; das Diktieren kommt dazu — und dass nichts ungefragt geschrieben
    // wird, gehört in denselben Halbsatz, sonst liest sich „ändern lassen" wie
    // ein Freibrief.
    zeile:
      'Lotte erzählt dir, woran ihr vorbeifahrt — und schreibt ins Tagebuch oder ändert die Reise, wenn du zustimmst; Landeswissen zu Trinkgeld und Notruf',
    gruppe: 'unterwegs',
    einleitung:
      'Lotte erzählt, hört zu und schreibt mit, wenn du zustimmst.'
  },
  {
    id: 'mitlesen',
    titel: 'Mitlesen lassen',
    // „Kapitelbild" steht seit dem 20.9.2026 dabei, und mit eigenen Wörtern:
    // Wer das Bild über einer Gegend ändern will, sucht es unter genau diesem
    // Wort — nicht unter „Mitlesen" und nicht unter „Titelbild".
    zeile:
      'der Link für Familie und Freunde — und was er nicht zeigt; Kapitelbild je Gegend selbst wählen',
    gruppe: 'unterwegs',
    einleitung:
      'Der Link für Familie und Freunde — und was er bewusst nicht zeigt.'
  },
  // Hinter allem, was unterwegs passiert, und vor dem Mitlesen. Beide Nachbarn
  // sind mit Bedacht gewählt: Erfasst werden Ausgaben unterwegs (Diktat,
  // Kassenzettel), ausgewertet werden sie danach — der Abschnitt gehört also
  // ans Ende der Fahrt und nicht in die Planung, obwohl die Planseite dort
  // entsteht. Und der letzte Absatz sagt, dass davon nichts nach außen geht;
  // direkt darunter beginnt der Abschnitt, in dem etwas nach außen geht.
  {
    id: 'kosten',
    titel: 'Was die Reise kostet',
    // „Maut, Vignetten am Fahrzeug" steht als Suchfutter mit drin: Der
    // Abschnitt erklärt seit dem 12.9.2026 den ganzen Mautkatalog, und wer in
    // der Befehlssuche „Vignette" tippt, sucht genau das — nicht die
    // Kostenübersicht.
    zeile:
      'geplant gegen wirklich — eintragen, diktieren, fotografieren, Maut, Vignetten am Fahrzeug, Währung, Dollar und Kurs am Belegtag',
    gruppe: 'danach',
    einleitung:
      'Geplant gegen wirklich: eintragen, diktieren, fotografieren, abrechnen.'
  },
  {
    id: 'nach-der-reise',
    titel: 'Nach der Reise',
    zeile: 'abschließen, und was sich dadurch ändert',
    gruppe: 'danach',
    einleitung:
      'Eine Reise abschließen — und was sich dadurch ändert.'
  },
  // Direkt hinter dem Abschließen, weil es dessen Folge ist: Den Knopf gibt es
  // nur an abgeschlossenen Reisen, und wer den Abschnitt darüber gelesen hat,
  // steht genau an der Stelle, an der die Frage aufkommt. Ein eigener
  // Abschnitt und kein Absatz dort, weil das Buch etwas anderes ist als die
  // Bilanz — die steht auf der geteilten Seite, das Buch geht an niemanden.
  {
    id: 'das-reisebuch',
    titel: 'Das Reisebuch',
    zeile:
      'die gefahrene Reise als gesetztes PDF, unter Erinnern › Reisebuch — Magazin mit Umschlag, Routen-Doppelseite, Kapitelauftakt, Randspalte, Großzitat, Foto-Doppelseiten, Collage, Höhenprofil, Fazit als eigenes Kapitel, Bildunterschrift mit Beschreibung; Buchtitel, Vorspann, Untertitel, vier Vorlagen, ganzes Buch oder Auswahl, Themen wie Planungsnotizen, Fassung für Bildschirm und Druck, Druckdienst, neu setzen, Fortschritt im Hintergrund, Zitat von Lotte, Kosten aus, Durchblättern, Seitenvorschau, Lupe, Fotos feinjustieren, Vollseite, Textfoto, Reihenfolge der Fotos, Auflösung, Warnung, Karte, Kapitel, Kapitelbild, Fotos, Bilanz',
    gruppe: 'danach',
    einleitung:
      'Die gefahrene Reise als Magazin im PDF — ganz oder als Auswahl, für Bildschirm und Druck, beliebig oft neu gesetzt.'
  },
  // Das einzige Kapitel, dessen Text am 15.9.2026 neu geschrieben wurde —
  // und auch das nur in der Frageform: Jede Antwort ist drei Sätze lang und
  // endet an der Stelle, an der das zuständige Kapitel ausführlich wird.
  // Warum überhaupt: Ein Handbuch ist nach Sachgebieten geordnet, Fragen sind
  // es nicht. „Warum fährt die Route über den Pass" steht sachlich in drei
  // Kapiteln und wird unter keinem davon gesucht.
  //
  // „FAQ" steht in der Zeile und nicht im Titel: Gesucht wird die Seite unter
  // beidem, und die Zeile ist das Suchfutter der Befehlssuche (siehe
  // `befehle.ts`) — die Überschrift bleibt deutsch.
  {
    id: 'faq',
    titel: 'Häufige Fragen',
    zeile:
      'FAQ: erste Reise, Route rechnen, Ladeplanung, Ladekarten, Vignetten, ZTL, Zeitzonen, Währungen, Länder, Lotte im Auto, Stopp, Mitlesen, Buchungen, Konto-Export',
    gruppe: 'nachschlagen',
    einleitung:
      'Die Fragen, die am Anfang immer kommen — kurz beantwortet, mit dem Weg ins Kapitel.'
  },
  {
    id: 'begriffe',
    titel: 'Kleines Nachschlagewerk',
    zeile:
      'Station, Durchfahrtspunkt, Etappe, Tagesplan, Anker, Reiseart, Ausgabe, Expertenansicht',
    gruppe: 'nachschlagen',
    einleitung:
      'Die Wörter, die in dieser Anwendung eine feste Bedeutung haben.'
  },
  // Das Welterbe-Album (21.10.2026): eine eigene Anwendung unter `/welterbe/`,
  // die kein eigenes Handbuch hat — ihre Anmeldeseite verweist hierher.
  {
    id: 'welterbe',
    titel: 'Das Welterbe-Album',
    zeile: 'alle UNESCO-Welterbestätten auf Karte und Liste, besucht, vorgemerkt, offen; Teilorte, Fortschritt, Zugang',
    gruppe: 'nachschlagen',
    einleitung:
      'Ein Sammelalbum der Welterbestätten mit eigener Adresse und demselben Konto: abhaken, vormerken, nachschlagen.'
  },
  {
    id: 'was-neu-ist',
    titel: 'Was sich geändert hat',
    zeile: 'die jüngsten Neuerungen, jeweils mit Datum',
    gruppe: 'nachschlagen',
    einleitung:
      'Was sich an der Anwendung geändert hat — nach Monat geordnet, neueste zuerst.'
  }
];

/**
 * Der Titel eines Abschnitts — für die Überschrift im Markup.
 *
 * Ohne diesen Umweg stünde jeder Titel zweimal da: einmal im Verzeichnis,
 * einmal über dem Abschnitt. Das läuft auseinander, und zwar in der
 * unangenehmen Richtung — das Verzeichnis verspricht dann etwas, das der
 * Abschnitt nicht hält.
 *
 * Wirft bei einem unbekannten Anker, statt leer zu bleiben: Eine Überschrift,
 * die still verschwindet, sieht auf der fertigen Seite aus wie ein
 * Gestaltungsfehler und nicht wie ein Tippfehler.
 */
export function titel(id: string): string {
  const abschnitt = GLIEDERUNG.find((a) => a.id === id);
  if (!abschnitt) throw new Error(`Kein Hilfeabschnitt „${id}“.`);
  return abschnitt.titel;
}

/** Das Kapitel zu dieser Kennung — oder `null`, wenn es keines gibt. */
export function kapitel(id: string | null | undefined): Hilfeabschnitt | null {
  if (!id) return null;
  return GLIEDERUNG.find((a) => a.id === id) ?? null;
}

/** Gibt es ein Kapitel mit dieser Kennung? Die Weiche in `wege.ts` fragt so. */
export function istKapitel(id: string): boolean {
  return GLIEDERUNG.some((a) => a.id === id);
}

/**
 * Die Kapitel nach Gruppen — die Leiste liest genau das.
 *
 * Gefiltert wird über `GRUPPEN` und nicht über die Reihenfolge der Kapitel:
 * So steht die Ordnung der Gruppen an einer Stelle, und ein Kapitel, das
 * versehentlich zwischen zwei Gruppen einsortiert wird, landet trotzdem bei
 * seiner eigenen statt eine dritte Gruppe mit demselben Namen zu erzeugen.
 */
export function nachGruppen(): { titel: string; kapitel: Hilfeabschnitt[] }[] {
  return GRUPPEN.map((g) => ({
    titel: g.titel,
    kapitel: GLIEDERUNG.filter((a) => a.gruppe === g.id)
  }));
}

/**
 * Was vor und nach diesem Kapitel kommt — für „Weiter" und „Zurück".
 *
 * Über die ganze Liste und nicht innerhalb der Gruppe: Wer am Ende von
 * „Anfangen" weiterblättert, will nach „Planen" und nicht in eine Sackgasse.
 * Am Anfang und am Ende bleibt eine Seite ohne Nachbarn — dort steht dann
 * ein Knopf weniger und nicht einer, der nichts tut.
 */
export function nachbarn(id: string): {
  vorher: Hilfeabschnitt | null;
  nachher: Hilfeabschnitt | null;
} {
  const i = GLIEDERUNG.findIndex((a) => a.id === id);
  if (i < 0) return { vorher: null, nachher: null };
  return {
    vorher: i > 0 ? GLIEDERUNG[i - 1] : null,
    nachher: i < GLIEDERUNG.length - 1 ? GLIEDERUNG[i + 1] : null
  };
}

/**
 * Der Einleitungssatz eines Kapitels — für den Kopf der Kapitelseite.
 *
 * Steht in derselben Form da wie `titel()` und aus demselben Grund: Der Satz
 * gehört zum Kapitel und nicht zur Hülle, und wer ihn zweimal hinschreibt
 * (einmal in der Leiste, einmal auf der Seite), hat ihn eines Tages zweimal
 * verschieden. Wirft bei unbekannter Kennung, damit ein Tippfehler auffällt
 * und nicht als leere Zeile unter der Überschrift steht.
 */
export function einleitung(id: string): string {
  const abschnitt = GLIEDERUNG.find((a) => a.id === id);
  if (!abschnitt) throw new Error(`Kein Hilfeabschnitt „${id}“.`);
  return abschnitt.einleitung;
}
